import { useState } from "react";
import Background from "../../../components/Background";
import Topnav from "../../../components/Topnav";
import { Box, Typography, Button } from "@mui/material";
import { languagesSpoken } from "../../../constants/CreateProfileLists";
import CopyRightTextMain from "../../../components/CopyRightTextMain";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateFields } from "../../../redux/profileSlice";
import RoundedTextField from "../../../components/RoundedProfileTextField";
import { profileOptionButtonStyles } from "./styles";
import { useEditableProfile } from "../../../hooks/useEditableProfile";

const EditLanguageSpoken = () => {
  // Router state when opened from the Profile UI, the stored profile on a
  // reload or a pasted link (ECH-387). `RequireProfileLoaded` guarantees one
  // of them exists before this component mounts, so the initialisers below
  // are safe to dereference.
  const { profile: localProfile } = useEditableProfile();
  const profile = localProfile?.profile ?? {};
  const [selected, setSelected] = useState(profile.languagesSpoken ?? []);

  const [customLanguages, setCustomLanguages] = useState(
    profile.languagesSpoken?.includes("Other") ? profile.customLanguages : "",
  );
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSelect = (language) => {
    const lang = selected.includes(language)
      ? selected.filter((value) => value !== language)
      : [...selected, language];

    setSelected(lang);
    if (!lang.includes("Other")) {
      setCustomLanguages("");
      setErrorMsg("");
    }
    const value = lang;
    dispatch(updateFields({ field: "languagesSpoken", value }));
  };

  if (!localProfile) {
    return <div>No profile data found.</div>;
  }

  return (
    <Background>
      <Topnav />
      <Box
        className="selection-box"
        sx={{
          maxWidth: {
            xs: "95%",
            sm: "550px",
            md: "550px",
            lg: "640px",
          },
        }}
      >
        <Typography
          className="text-primary"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
              lg: "26px",
              xl: "32px",
            },
            lineHeight: {
              xs: "28px",
              sm: "30px",
              md: "24px",
              lg: "38px",
              xl: "42px",
            },
            marginBottom: "5px",
          }}
        >
          Languages Spoken
        </Typography>
        <Typography
          variant="body"
          display="inline-block"
          sx={{
            fontWeight: 400,
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "14px",
              lg: "16px",
            },
            color: "#152134",
            paddingBottom: "20px",
          }}
        >
          To make mentorship as accessible as possible, we want to know which
          languages you speak. This helps us display potential matches who share
          the same languages, ensuring better communication and support.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          {languagesSpoken.map((language) => (
            <Button
              //className="hover-tag-color"
              key={language}
              style={{
                ...profileOptionButtonStyles,
                backgroundColor: selected.includes(language)
                  ? "#7BB9D5"
                  : "#ffffff",
                borderColor: selected.includes(language)
                  ? "#7BB9D5"
                  : "#AAB6C8",
                fontWeight: selected.includes(language) ? 600 : 400,
              }}
              onClick={() => handleSelect(language)}
              sx={{
                fontSize: {
                  xs: "12px",
                  sm: "14px",
                },
                padding: {
                  xs: "10px 15px",
                  sm: "10px 15px",
                  md: "15px 25px",
                },
              }}
            >
              {language}
            </Button>
          ))}
        </Box>
        <Box>
          {selected.includes("Other") && (
            <Box
              sx={{
                marginTop: {
                  xs: "20px",
                  sm: "20px",
                  md: "20px",
                  lg: "20px",
                  xl: "30px",
                },
              }}
            >
              <Typography
                sx={{
                  textAlign: "left",
                  fontSize: {
                    xs: "12px",
                    sm: "13px",
                    md: "14px",
                  },
                  fontWeight: 400,
                  marginBottom: "5px",
                  color: "#152134",
                }}
              >
                Please Specify
              </Typography>
              <RoundedTextField
                name="customLanguages"
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                value={customLanguages}
                onChange={(e) => {
                  setCustomLanguages(e.target.value);
                }}
                slotProps={{
                  input: {
                    "aria-label": "custom Languages input",
                  },
                }}
              />
            </Box>
          )}
        </Box>
        {errorMsg && <Typography sx={{ color: "red" }}>{errorMsg}</Typography>}
        <Button
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: selected ? "#2A4876" : "#AAB6C8", // Change color based on input values
            cursor: selected ? "pointer" : "not-allowed", // Disable hover effect when fields are not filled
            color: "#fff",
            marginTop: "20px !important",
            width: {
              xs: "100%",
              sm: "80%",
              md: "80%",
              lg: "370px",
            },
            height: {
              xs: "45px",
              sm: "50px",
              md: "55px",
              lg: "60px",
              xl: "70px",
            },
            fontSize: {
              xs: "15px",
              sm: "15px",
              md: "16px",
              lg: "18px",
            },
            fontWeight: 600,
            borderRadius: "50px",
            textTransform: "capitalize",
          }}
          disabled={
            selected.includes("Other")
              ? customLanguages === ""
              : selected.length < 1
          }
          onClick={() => {
            if (selected.includes("Other")) {
              console.log("custom Languages are", customLanguages);
              const filteredLanguagesList = languagesSpoken.map((language) =>
                language.toLocaleLowerCase(),
              );
              if (
                filteredLanguagesList.includes(
                  customLanguages.toLocaleLowerCase(),
                )
              ) {
                setErrorMsg(
                  "Invalid entry: Please enter a language that is not listed in the options.",
                );
              } else {
                dispatch(
                  updateFields({
                    field: "customLanguages",
                    value: customLanguages,
                  }),
                );
                dispatch(
                  updateFields({
                    field: "languagesSpoken",
                    value: selected,
                  }),
                );
                navigate("/profile/edit-profile");
              }
            } else {
              navigate("/profile/edit-profile");
            }
          }}
        >
          Save
        </Button>
      </Box>
      <CopyRightTextMain />
    </Background>
  );
};

export default EditLanguageSpoken;
